export interface SessionMessage {
  // Row id. Optional because the sessions API has long served messages without it; the
  // live socket sends it on every message, and it is how a removed turn is dropped from a
  // thread that is still open (two interim transcripts of the same turn can share a
  // timestamp, so the timestamp alone is not a handle).
  id?: string;
  type: "user" | "agent" | "tool" | "event" | "human";
  value: string;
  event?: string; // Optional event name for system messages (e.g., greetings, silence)
  timestamp: number;
  tool?: {
    name: string;
    call_id: string;
    input: {
      [key: string]: string;
    };
  };
  metadata?: {
    hooks?: string[]; // Filler hooks played out during this agent turn (debugging)
    [key: string]: unknown;
  };
}

export interface SessionContext {
  ip?: string;
  user_agent?: string;
  /** ISO 3166-1 alpha-2 ("FR"): the carrier's for a call, the CDN's for a web visitor. */
  country_code?: string;
  /** A web visitor's Accept-Language, most preferred first. */
  browser_languages?: string[];
  /** The site the web widget was embedded on. */
  page_origin?: string;
  /** "web_widget" for the embeddable public chat; "whatsapp" | "messenger" | "instagram" on Meta. */
  channel?: string;
  /**
   * Where a Meta conversation lives: who wrote last and when — the free-form
   * window shuts 24 hours after `last_customer_message_at`.
   */
  meta_channel?: {
    connector_id: string;
    channel: "whatsapp" | "messenger" | "instagram";
    sender_name?: string;
    last_customer_message_at?: number;
  };
  language?: string;
  phone_number?: string;
  email?: string;
  customer_id?: string;
  timezone?: string;
  /**
   * Set once Asterisk's recording of a team member on the phone has landed — after
   * a transfer, or a handset's own call.
   */
  human_recording?: {
    kind: "transfer" | "device";
    received_at: string;
    duration_s: number | null;
    status?: "received" | "transcribed" | "failed";
    error?: string;
  };
  /** A call a handset took or placed without the agent. */
  handset_call?: {
    direction: "inbound" | "outbound";
    device_label?: string;
    member?: { name?: string };
  };
}

/**
 * What a conversation did, each put by a tool that succeeded (backend
 * `services/session/tags.ts`). Older rows may still carry `booking`, `information`
 * or `abandoned`, which are no longer shown — see `shownTags`.
 */
export type SessionTags =
  | "booking_creation"
  | "booking_cancellation"
  | "booking_reschedule"
  | "transfer"
  | "qualification_form";

/** A session's single dashboard classification — `other` when nothing tagged it. */
export type SessionClassification = SessionTags | "no_answer" | "other";

/**
 * Who an administrator's message goes out as, from a conversation's history:
 * `admin` — an instruction to the agent, never shown to the customer;
 * `user` — as if the customer had typed it; `agent_sms` — texted to the
 * customer in the agent's name.
 */
export type AdminSendAs = "admin" | "user" | "agent_sms";

export interface Session {
  id: string;
  public_id?: string; // Optional for backoffice sessions
  client_id: string;
  customer_id: string;
  agent_id: string;
  type: "inbound" | "outbound" | "notifications";
  medium: "call" | "chat" | "email";
  state: "open" | "closed";
  // Nullable, and the type has to say so: the column is optional server-side, so a
  // row closed without one comes back `null` — which used to reach a `t()` default
  // and crash the list rather than read as `unknown`.
  result: "success" | "failure" | "no_answer" | "voicemail" | "unknown" | null;
  sentiment: "positive" | "neutral" | "negative";
  purpose: string;
  name: string;
  summary: string;
  tags: SessionTags[];
  duration: number;
  context: SessionContext;
  messages: SessionMessage[];
  created_at: string;
  updated_at: string;
  // When the caller or the agent last said something (ms) — what the history is sorted
  // on. 0 on a row the server has not stamped yet; see `lastActivityAt`.
  last_message_at?: number;
  previous_sessions?: Session[]; // Previous sessions in transfer chain
  last_transfer_session_id?: string | null; // Last session in forward transfer chain
  last_transfer_session_public_id?: string | null; // Public ID of last session in forward transfer chain
  // Handed to a person during the conversation (a member, a shared phone, the HeyUp
  // team) — computed by the server from the transfer tool rows, see
  // `session/outcome.ts`. A hand-over to another AI agent does not count.
  transferred?: boolean;
  // Some tool of the conversation failed every time it was called — what makes a
  // `failure` verdict one (`effectiveResult`). Computed by the server, same file.
  tool_failed?: boolean;
}

// Session without messages (for list views)
export type SessionListItem = Omit<Session, "messages">;

export interface SearchSessionsOptions {
  /** Comma-separated session ids: a known set of conversations. */
  ids?: string;
  customer_id?: string;
  agent_id?: string;
  state?: "open" | "closed";
  type?: "inbound" | "outbound" | "notifications";
  medium?: "call" | "chat" | "email";
  tags?: string;
  // "conversations" hides what nobody answered (`result: "no_answer"` — an inbound call
  // nobody spoke in, an outbound leg that rang out), "no_answer" shows only that.
  category?: "conversations" | "no_answer";
  search?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

export interface SearchSessionsResult {
  data: SessionListItem[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface SessionStats {
  total_sessions: {
    call: number;
    chat: number;
    email: number;
  };
  total_minutes: {
    call: number;
    chat: number;
    email: number;
  };
  total_messages: {
    call: number;
    chat: number;
    email: number;
  };
  sessions_by_day: {
    date: string;
    count: number;
  }[];
  // One bucket per session: the backend keeps only the most specific tag of a
  // session, so the slices add up to the session count instead of counting a
  // booking twice (once as `booking`, once as `booking_creation`).
  sessions_by_tag: {
    tag: SessionClassification;
    count: number;
  }[];
  // Bucketed like `effectiveResult` (`features/sessions/result.ts`) so the
  // dashboard's slices match the calls list's badges.
  sessions_by_result: {
    result:
      | "success"
      | "failure"
      | "no_answer"
      | "voicemail"
      | "transferred"
      | "unknown"
      | "pending";
    count: number;
  }[];
  sessions_by_sentiment: {
    sentiment: "positive" | "neutral" | "negative";
    count: number;
  }[];
  unique_customers: number;
  total_value: number;
  total_value_by_medium: {
    call: number;
    chat: number;
    email: number;
  };
  average_duration: number; // in seconds
  average_duration_by_medium: {
    call: number;
    chat: number;
    email: number;
  }; // in seconds
  // Inbound calls that came in while the store was closed, and what was booked
  // on them. `null` when the tenant never set its opening hours. Optional for a
  // server that predates it.
  after_hours?: {
    calls: number;
    value: number;
    total_calls: number;
  } | null;
}

/**
 * When a conversation last moved, as the history reads it: the last thing somebody said,
 * not `updated_at`, which a wrap-up or a metrics refresh moves minutes after the call.
 */
export const lastActivityAt = (
  session: Pick<Session, "last_message_at" | "updated_at">,
): Date =>
  new Date(session.last_message_at || new Date(session.updated_at).getTime());
